'use client';
import React, { useMemo } from 'react';
import type { EChartsOption } from 'echarts';
import type { OrganizeOptionDataReturn, ExpiryDates } from '@benzinga/data-option-chain';
import { DateTime } from 'luxon';
import { ChartPlotSkeleton } from '../QuoteSkeletons';
const ReactECharts = React.lazy(() => import('echarts-for-react'));

const CALL_COLOUR = '#4B8BF4';
const PUT_COLOUR = '#E05252';
const CALL_OI_COLOUR = '#4B8BF4';
const PUT_OI_COLOUR = '#E05252';

type StrikeTotals = { callVol: number; putVol: number; callOI: number; putOI: number };

const formatExpiryShort = (key: string): string => {
  const expiryDate = DateTime.fromFormat(key, 'yyyyMMdd');
  return expiryDate.isValid ? expiryDate.toFormat('MMM d') : key;
};

const shortK = (value: number): string => (value >= 1000 ? `${(value / 1000).toFixed(0)}K` : String(value));

interface VolumeVsOIByStrikeProps {
  expiryKey: string;
  optionChainData: OrganizeOptionDataReturn;
}

export const VolumeVsOIByStrikeChart: React.FC<VolumeVsOIByStrikeProps> = ({ expiryKey, optionChainData }) => {
  const option = useMemo((): EChartsOption => {
    const chains = optionChainData.dataByExpiry[expiryKey] ?? [];
    const strikeMap = new Map<number, StrikeTotals>();

    for (const chain of chains) {
      for (const call of chain.calls ?? []) {
        const strike = call.strike;
        const prev = strikeMap.get(strike) ?? { callVol: 0, putVol: 0, callOI: 0, putOI: 0 };
        strikeMap.set(strike, {
          ...prev,
          callVol: prev.callVol + (call.volume ?? 0),
          callOI: prev.callOI + (call.openInterest ?? 0),
        });
      }
      for (const put of chain.puts ?? []) {
        const strike = put.strike;
        const prev = strikeMap.get(strike) ?? { callVol: 0, putVol: 0, callOI: 0, putOI: 0 };
        strikeMap.set(strike, {
          ...prev,
          putVol: prev.putVol + (put.volume ?? 0),
          putOI: prev.putOI + (put.openInterest ?? 0),
        });
      }
    }

    const strikes = Array.from(strikeMap.keys()).sort((left, right) => left - right);
    const callVolData = strikes.map(strike => strikeMap.get(strike)!.callVol);
    const putVolData = strikes.map(strike => strikeMap.get(strike)!.putVol);
    const callOIData = strikes.map(strike => strikeMap.get(strike)!.callOI);
    const putOIData = strikes.map(strike => strikeMap.get(strike)!.putOI);

    return {
      grid: { left: 48, right: 16, top: 16, bottom: 48 },
      legend: {
        data: ['Call Vol', 'Put Vol', 'Call OI', 'Put OI'],
        bottom: 0,
        textStyle: { fontSize: 11 },
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: unknown) => {
          const rows = Array.isArray(params) ? params : [params];
          const strike = (rows[0] as { axisValue?: string })?.axisValue ?? '';
          const body = rows
            .map(rowParam => {
              const row = rowParam as { marker: string; seriesName: string; value: number };
              return `${row.marker}${row.seriesName}: ${shortK(row.value)}`;
            })
            .join('<br/>');
          return `<b>$${strike}</b><br/>${body}`;
        },
      },
      xAxis: {
        type: 'category',
        data: strikes.map(strike => `$${strike}`),
        axisLabel: { fontSize: 10, interval: Math.floor(strikes.length / 10) },
      },
      yAxis: [
        {
          type: 'value',
          axisLabel: { formatter: shortK, fontSize: 10 },
          splitLine: { lineStyle: { color: '#f0f0f0' } },
        },
        { type: 'value', axisLabel: { formatter: shortK, fontSize: 10 }, splitLine: { show: false } },
      ],
      series: [
        {
          name: 'Call Vol',
          type: 'bar',
          data: callVolData,
          itemStyle: { color: `${CALL_COLOUR}99` },
          yAxisIndex: 0,
        },
        {
          name: 'Put Vol',
          type: 'bar',
          data: putVolData,
          itemStyle: { color: `${PUT_COLOUR}99` },
          yAxisIndex: 0,
        },
        {
          name: 'Call OI',
          type: 'line',
          data: callOIData,
          lineStyle: { color: CALL_OI_COLOUR, type: 'dashed', width: 1.5 },
          symbol: 'none',
          yAxisIndex: 1,
        },
        {
          name: 'Put OI',
          type: 'line',
          data: putOIData,
          lineStyle: { color: PUT_OI_COLOUR, type: 'dashed', width: 1.5 },
          symbol: 'none',
          yAxisIndex: 1,
        },
      ],
    } as EChartsOption;
  }, [optionChainData, expiryKey]);

  return (
    <div className="rounded border border-[#e9ecef] bg-white p-4">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Volume vs. Open Interest by Strike
      </p>
      <React.Suspense fallback={<ChartPlotSkeleton />}>
        <ReactECharts option={option} style={{ height: 220 }} />
      </React.Suspense>
    </div>
  );
};

interface VolumeByExpirationProps {
  optionChainData: OrganizeOptionDataReturn;
}

export const VolumeByExpirationChart: React.FC<VolumeByExpirationProps> = ({ optionChainData }) => {
  const option = useMemo((): EChartsOption => {
    const { dataByExpiry, expiries } = optionChainData;

    const labels: string[] = [];
    const callVols: number[] = [];
    const putVols: number[] = [];

    for (const expiry of expiries as ExpiryDates[]) {
      const chains = dataByExpiry[expiry.value] ?? [];
      const usable = chains.some(chain => (chain.calls?.length ?? 0) > 0 && (chain.puts?.length ?? 0) > 0);
      if (!usable) continue;

      let callVol = 0;
      let putVol = 0;
      for (const chain of chains) {
        for (const call of chain.calls ?? []) callVol += call.volume ?? 0;
        for (const put of chain.puts ?? []) putVol += put.volume ?? 0;
      }
      labels.push(formatExpiryShort(expiry.value));
      callVols.push(callVol);
      putVols.push(putVol);
    }

    return {
      grid: { left: 48, right: 16, top: 16, bottom: 48 },
      legend: {
        data: ['Call Vol', 'Put Vol'],
        bottom: 0,
        textStyle: { fontSize: 11 },
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: unknown) => {
          const rows = Array.isArray(params) ? params : [params];
          const axisValue = (rows[0] as { axisValue?: string })?.axisValue ?? '';
          const body = rows
            .map(rowParam => {
              const row = rowParam as { marker: string; seriesName: string; value: number };
              return `${row.marker}${row.seriesName}: ${shortK(row.value)}`;
            })
            .join('<br/>');
          return `<b>${axisValue}</b><br/>${body}`;
        },
      },
      xAxis: { type: 'category', data: labels, axisLabel: { fontSize: 11 } },
      yAxis: {
        type: 'value',
        axisLabel: { formatter: shortK, fontSize: 10 },
        splitLine: { lineStyle: { color: '#f0f0f0' } },
      },
      series: [
        {
          name: 'Call Vol',
          type: 'bar',
          data: callVols,
          itemStyle: { color: CALL_COLOUR },
        },
        {
          name: 'Put Vol',
          type: 'bar',
          data: putVols,
          itemStyle: { color: PUT_COLOUR },
        },
      ],
    } as EChartsOption;
  }, [optionChainData]);

  return (
    <div className="rounded border border-[#e9ecef] bg-white p-4">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Volume by Expiration</p>
      <React.Suspense fallback={<ChartPlotSkeleton />}>
        <ReactECharts option={option} style={{ height: 220 }} />
      </React.Suspense>
    </div>
  );
};
